<script setup lang="ts">
  import { computed } from 'vue';
  import {
    getNoticeCategoryLabel,
    getNoticeNavigationTarget,
    getNoticeStatusClass,
    getNoticeStatusLabel,
    isGeneralHistoryNotice,
    isMyHistoryNotice,
    type NoticeListItem,
  } from '@/entities/ReminderHistory';
  import ArrowDownIcon from '@/shared/assets/icons/ArrowDown.svg';
  import TableTrashIcon from '@/shared/assets/icons/TableTrashIcon.svg';
  import { Checkbox, IconImage, NoticeMessage } from '@/shared/ui';
  import { pluralize } from '@/shared/lib';

  interface Props {
    notice: NoticeListItem;
    showGeneralColumns: boolean;
    mapDate: (value: string | null | undefined) => string;
    isExpanded?: boolean;
    isSelected?: boolean;
    isAcknowledgePending?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    isExpanded: false,
    isSelected: false,
    isAcknowledgePending: false,
  });

  const emit = defineEmits<{
    (e: 'open-recipients', noticeId: number): void;
    (e: 'toggle-selection', noticeId: number): void;
    (e: 'acknowledge', noticeId: number): void;
    (e: 'delete', noticeId: number): void;
  }>();

  const navigationTarget = computed(() => getNoticeNavigationTarget(props.notice));

  const messageCardClass = computed(() =>
    props.showGeneralColumns ? 'text-sm font-normal leading-5' : 'text-sm leading-5'
  );

  const generalStatusLabel = computed(() => {
    if (!isGeneralHistoryNotice(props.notice)) {
      return '-';
    }

    return props.notice.acknowledgedCount === props.notice.recipientCount
      ? 'Visualizado'
      : 'Não visualizado';
  });

  const generalStatusClass = computed(() => {
    if (!isGeneralHistoryNotice(props.notice)) {
      return 'text-gray-07';
    }

    return props.notice.acknowledgedCount === props.notice.recipientCount
      ? 'bg-green-20 text-gray-07'
      : 'bg-badge-orange-30 text-badge-orange';
  });

  const isMyHistoryAcknowledged = computed(
    () => isMyHistoryNotice(props.notice) && props.notice.acknowledged
  );

  const acknowledgeButtonClass = computed(() => {
    if (props.isAcknowledgePending) {
      return 'bg-[var(--theme-secondary)] text-white opacity-70 cursor-not-allowed';
    }

    return isMyHistoryAcknowledged.value
      ? 'bg-gray-02 text-white cursor-not-allowed shadow-none'
      : 'bg-[var(--theme-secondary)] text-white shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] hover:opacity-90';
  });
</script>

<template>
  <tr class="border-t border-gray-02 align-center">
    <td
      v-if="showGeneralColumns"
      class="pl-6 align-middle text-center"
    >
      <Checkbox
        :id="notice.id"
        :checked="props.isSelected"
        @toggle="emit('toggle-selection', notice.id)"
      />
    </td>
    <td class="px-4 py-3">
      <div
        data-testid="notice-message-card"
        class="max-w-61 rounded-lg border border-gray-02 bg-white-01 px-3 py-3 text-gray-03"
        :class="messageCardClass"
      >
        <NoticeMessage
          :message="notice.message"
          :label="notice.relatedEntityName"
          :to="navigationTarget"
        />
      </div>
    </td>
    <td class="px-4 py-3 text-xs leading-5 text-gray-07">
      {{ mapDate(notice.createdAt) }}
    </td>
    <td class="px-4 py-3 text-xs leading-5 text-gray-07">
      {{ notice.sender?.name ?? '-' }}
    </td>
    <td class="px-4 py-3 text-xs leading-5 text-gray-07">
      {{ notice.sender?.position ?? '-' }}
    </td>
    <td
      v-if="showGeneralColumns"
      class="whitespace-nowrap px-4 py-3 text-xs leading-5 text-gray-07"
    >
      {{
        isGeneralHistoryNotice(notice)
          ? `${notice.recipientCount} ${pluralize(notice.recipientCount, 'colaborador', 'colaboradores')}`
          : '-'
      }}
    </td>
    <td
      v-if="showGeneralColumns"
      class="whitespace-nowrap px-4 py-3 text-xs leading-5 text-gray-07"
    >
      {{ isGeneralHistoryNotice(notice) ? notice.acknowledgedSummary : '-' }}
    </td>
    <td class="px-4 py-3 text-xs leading-5 text-gray-07">
      {{ getNoticeCategoryLabel(notice.category) }}
    </td>
    <td class="px-4 py-3">
      <template v-if="showGeneralColumns">
        <span
          class="inline-flex items-center justify-center rounded-full px-[7px] py-[2px] text-xs font-medium leading-[18px]"
          :class="generalStatusClass"
        >
          {{ generalStatusLabel }}
        </span>
      </template>
      <template v-else-if="isMyHistoryNotice(notice)">
        <span
          class="inline-flex items-center justify-center rounded-full px-[7px] py-[2px] text-xs font-medium leading-[18px]"
          :class="getNoticeStatusClass(notice)"
        >
          {{ getNoticeStatusLabel(notice) }}
        </span>
      </template>
      <span
        v-else
        class="text-xs leading-5 text-gray-07"
      > - </span>
    </td>
    <td class="whitespace-nowrap px-4 py-3">
      <template v-if="showGeneralColumns">
        <div class="flex items-center gap-10">
          <button
            type="button"
            data-testid="row-delete-action"
            class="inline-flex h-6 w-6 items-center justify-center text-gray-xx-02"
            @click="emit('delete', notice.id)"
          >
            <IconImage
              :src="TableTrashIcon"
              alt="Excluir aviso"
              class="w-6 h-6"
            />
          </button>
          <button
            type="button"
            data-testid="row-more-actions"
            class="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-02 bg-white-01 text-gray-xx-02"
            @click="emit('open-recipients', notice.id)"
          >
            <IconImage
              :src="ArrowDownIcon"
              alt="Mais ações"
              class="h-5 w-5 transition-transform duration-200"
              :class="props.isExpanded ? 'rotate-180' : 'rotate-0'"
            />
          </button>
        </div>
      </template>
      <template v-else-if="isMyHistoryNotice(notice)">
        <button
          type="button"
          data-testid="row-acknowledge-action"
          class="inline-flex h-[42px] min-w-[105px] items-center justify-center rounded-lg px-4 text-sm font-medium transition-opacity"
          :class="acknowledgeButtonClass"
          :disabled="props.isAcknowledgePending || notice.acknowledged"
          @click="emit('acknowledge', notice.id)"
        >
          {{ props.isAcknowledgePending ? 'Confirmando...' : 'Estou ciente' }}
        </button>
      </template>
      <span
        v-else
        class="text-xs leading-5 text-gray-07"
      > - </span>
    </td>
  </tr>
</template>
